/**
 * Report a Speedy (F-129; A plan §4.9a). A drawer on a phone, a dialog on desktop. Reuses the one
 * report path (`useReportContent` → `message_reports`, with `speedyId`) and the one list of
 * reasons. Never shown on the member's own Speedy (the caller hides the entry points).
 *
 * Send is disabled until a reason is picked and while sending; a failure keeps the note; Escape
 * closes only this sheet (Radix layers it over the player).
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { ReportReason } from '@hermes/db/schema';
import { useReportContent } from '@/api/community-hooks';
import { REPORT_REASONS } from '@/components/community/report-reasons';
import { useIsMobile } from '@/hooks/use-mobile';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from '@/components/ui/drawer';

export const REPORT_NOTE_MAX = 500;

/** What a refused report means to the member. `null` = treat it as sent (already reported). */
export function reportErrorMessage(err: unknown): string | null {
  const e = err as { code?: string; message?: string } | null;
  if (e?.code === '23505') return null;
  if (/speedy report limit/i.test(e?.message ?? '')) {
    return "You've sent a lot of reports today. Try again tomorrow.";
  }
  if (e?.code === '42501' || /row-level security/i.test(e?.message ?? '')) {
    return "This Speedy can't be reported.";
  }
  return "Couldn't send. Try again.";
}

interface SpeedyReportSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  speedyId: string;
  authorId: string;
}

export function SpeedyReportSheet({ open, onOpenChange, speedyId, authorId }: SpeedyReportSheetProps) {
  const isMobile = useIsMobile();
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const report = useReportContent();

  const close = () => {
    onOpenChange(false);
    setReason(null);
    setNote('');
    setProblem(null);
  };

  const send = () => {
    if (!reason || report.isPending) return;
    setProblem(null);
    report.mutate(
      { speedyId, reportedUserId: authorId, reason, details: note.trim() || undefined },
      {
        onSuccess: () => {
          toast.success('Thanks — our team will take a look');
          close();
        },
        onError: (err) => {
          const msg = reportErrorMessage(err);
          if (msg === null) {
            toast.success('Thanks — our team will take a look');
            close();
          } else {
            setProblem(msg);
          }
        },
      },
    );
  };

  const body = (
    <div className="space-y-4">
      <div role="radiogroup" aria-label="Reason" className="space-y-2">
        {REPORT_REASONS.map((r) => (
          <button
            key={r.id}
            type="button"
            role="radio"
            aria-checked={reason === r.id}
            onClick={() => setReason(r.id)}
            className={`flex min-h-[44px] w-full items-center rounded-lg border px-3 text-left text-sm ${
              reason === r.id ? 'border-primary bg-primary/10' : 'border-border hover:bg-secondary'
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>
      <label className="block text-sm">
        <span className="mb-1 block text-muted-foreground">Anything else? (optional)</span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, REPORT_NOTE_MAX))}
          rows={3}
          className="w-full rounded-lg border border-border bg-background p-2 text-sm"
          maxLength={REPORT_NOTE_MAX}
        />
        <span className="mt-1 block text-right text-xs text-muted-foreground">{note.length}/{REPORT_NOTE_MAX}</span>
      </label>
      {problem && <p className="text-sm text-danger" role="alert">{problem}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={close} className="min-h-[44px] rounded-full px-4 text-sm">Cancel</button>
        <button
          type="button"
          onClick={send}
          disabled={!reason || report.isPending}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
        >
          {report.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
          Send report
        </button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
        <DrawerContent className="z-[110] px-4 pb-6">
          <DrawerTitle className="pt-4 text-base font-semibold">Report this Speedy</DrawerTitle>
          <DrawerDescription className="mb-4 text-sm text-muted-foreground">It stays up while our team looks.</DrawerDescription>
          {body}
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="z-[110] max-w-md">
        <DialogTitle>Report this Speedy</DialogTitle>
        <DialogDescription>It stays up while our team looks.</DialogDescription>
        {body}
      </DialogContent>
    </Dialog>
  );
}
